/**
 * ==========================================================================
 * Bộ lọc Template
 * ==========================================================================

    * Luồng xử lý:
        1. Lấy toàn bộ products từ Shopify.
        2. Loại bỏ các product có tag "Hypothesis".
        3. Áp dụng các điều kiện lọc:
            - Search (theo title)
            - Industry (product_type)
            - Tag (product.tags)
        4. Sắp xếp dữ liệu.
        5. Render danh sách template và cập nhật số lượng của từng tag.

 * --------------------------------------------------------------------------
 * Logic đếm số lượng Category (Tag)
 * --------------------------------------------------------------------------
    * "All templates":
        - Hiển thị tổng số product sau khi áp dụng:
            + Search
            + Industry
        - Không phụ thuộc Tag đang chọn.
     
    * Các category được tạo từ product tags, 
        ngoại trừ các tag trong ignoreCategoryTags.
     
    * Lưu ý:  Một product có thể chứa nhiều tag => Vì vậy tổng số của các category có thể lớn hơn "All templates". 
        ví dụ: Hydra [Landing page, Recommend]
        
    * Khi đó:
        + All templates = 1
        + Landing page = 1
        + Recommend = 1

 * --------------------------------------------------------------------------
 * Render dữ liệu
 * --------------------------------------------------------------------------
    * Chưa chọn Tag: Hiển thị tất cả section theo từng tag.
 
    * Đã chọn Tag: Chỉ hiển thị section của tag đó.

 * --------------------------------------------------------------------------
 * Show more
 * --------------------------------------------------------------------------
    * Khi đang ở All Templates:
        - Click "Show more" sẽ chuyển sang trang của tag tương ứng.
     
    * Ở trang tag:
        - Click "Show more" sẽ hiển thị thêm template của tag đó.
 
 * --------------------------------------------------------------------------
 * Đồng bộ URL
 * --------------------------------------------------------------------------
    * Tag được lưu trên query string (?tag=...) để:
        - Có thể reload hoặc chia sẻ URL.
        - Không cần reload khi đổi tag (History API).

 * --------------------------------------------------------------------------
 * Sắp xếp template. Thay đổi value trong:  TemplateFilter.config.currentSort
 * --------------------------------------------------------------------------
    - best-selling       : Recommend > Trending > Featured > Template thường > Không có các tag trước sẽ fallback theo ngày tạo mới nhất
    - created-descending : Template mới nhất trước
    - created-ascending  : Template cũ nhất trước
    - title-ascending    : Sắp xếp tên A → Z
    - title-descending   : Sắp xếp tên Z → A

 * ==========================================================================
*/

const TemplateFilter = {
    config: {
        defaultPerPage: 3,  // Số template mặc định ở All Template
        tagPerPage: 12,     // Số template hiển thị mỗi lần ở trang Tag

        apiLimit: 250, // Shopify products.json max limit

        ignoreProducts: ['hypothesis'],         // loại bỏ product chứa tag này
        ignoreCategoryTags: ['recommend'],  // không tạo category

        currentSort: 'created-descending',

        industriesTarget: '#js-industries-list',
        categoriesTarget: '#js-template-categories',
        searchInputTarget: '#js-tpl-search-input',
        filterSelected: '#js-filter-selected',

        // Sắp xếp page type, stt nhỏ lên trước
        PAGE_ORDER: {
            homepage: 0,
            'collection page': 1,
            'product page': 2,
            'blog page': 3,
            'blog post page': 4,
            'about page': 5,
            'faq page': 6,
            'contact page': 7
        }
    },

    state: {
        productList: [],
        currentProducts: [],

        productsIgnoreTagCount: 0,
        productsIgnoreTagMap: null,

        searchKeyword: '',
        filterSelected: [],
        visibleItems: {},
        isTagPage: false,

        fullTagMap: null, // cache tag map toàn bộ product
    },

    async init() {
        const { api, state, ui, events, filter, helpers } = this;

        state.productList = await api.fetchAllProducts();

        state.fullTagMap = ui.buildTagMap(state.productList);

        ui.updateTotalTemplates();

        const params = new URLSearchParams(window.location.search);
        const currentTag = params.get('tag');

        if (currentTag) {
            state.isTagPage = true;
            state.filterSelected.push({
                key: 'tag',
                value: helpers.unslugifyTag(currentTag)
            });
        }

        ui.renderCategoryButtons();
        events.init();
        filter.init();
    },

    ui: {

        renderProductItem(product) {
            const tagsHtml = (product.tags || [])
                .map(tag => `
                    <div class="tpl-card__badge ${tag === 'Recommend' ? '!order-[0]' : ''}">
                        ${tag === 'Recommend' ? TemplateIcon.recommendIcon : ''}
                        <span>${tag}</span>
                    </div>
                `).join('');

            const image = product.images?.[0];
            const imageSrc = TemplateFilter.helpers.getResizeImage(image?.src, 640);

            return `
                <article class="tpl-card">
                    <div class="tpl-card__thumb">

                        ${tagsHtml ? `
                            <div class="tpl-card__tags">
                                ${tagsHtml}
                            </div>
                        ` : ''}

                        <div class="w-full relative">
                            <img
                                src="${imageSrc}"
                                alt="${image?.alt || product.title}"
                                width="${image?.width || ''}"
                                height="${image?.height || ''}"
                                loading="lazy"
                                decoding="async"
                                class="w-full h-full object-cover object-top m-auto block"
                            >
                        </div>

                        <div class="tpl-card__hover">
                            <a href="/pages/templates/${product.handle}" class="tpl-card__use" >
                                <span>View Details</span>
                                ${TemplateIcon.arrowIcon}
                            </a>
                        </div>
                    </div>

                    <div class="tpl-card__body">
                        <a href="/pages/templates/${product.handle}">
                            <h3 class="tpl-card__title capitalize"> ${product.title} </h3>
                        </a>

                        <div class="tpl-card__body-row">
                            <span>${product.product_type}</span>
                        </div>
                    </div>
                </article>
            `;
        },

        buildTagMap(products) {
            const map = {};

            const { ignoreCategoryTags, PAGE_ORDER } = TemplateFilter.config

            products.forEach(product => {
                [...new Set(product.tags || [])].forEach(tag => {
                    const key = tag.toLowerCase().trim();

                    // Ignore specific tags
                    if (ignoreCategoryTags.includes(key)) {
                        return;
                    }

                    if (!map[key]) {
                        map[key] = {
                            label: tag,
                            products: []
                        };
                    }

                    map[key].products.push(product);
                });
            });

            return Object.fromEntries(

                Object.entries(map).sort((a, b) => {
                    const orderA = PAGE_ORDER[a[0]];
                    const orderB = PAGE_ORDER[b[0]];

                    // Both are page-type categories
                    if (orderA !== undefined && orderB !== undefined) {
                        return orderA - orderB;
                    }

                    // Page-type categories always come first
                    if (orderA !== undefined) {
                        return -1;
                    }

                    if (orderB !== undefined) {
                        return 1;
                    }

                    // Other categories remain alphabetical
                    return a[1].label.localeCompare(b[1].label);
                })
            );
        },

        renderCategoryButtons() {
            const { state, config, helpers, filter } = TemplateFilter;

            const holder = document.querySelector(config.categoriesTarget);
            if (!holder) return;

            const groups = state.fullTagMap;

            const countEl = document.getElementById('js-page-types-count');
            if (countEl) countEl.innerHTML = Object.entries(groups).length;

            let html = [`
                <button class="js-tpl-cat tpl-cat is-active" data-tag="">
                    ${industriesIcon.all_templates}

                    <span> All templates </span>

                    <span class="tpl-cat__count">
                        ${state.productList.length}
                    </span>
                </button>
            `];

            Object.entries(groups).forEach(([tag, group]) => {
                const list = group.products;
                const label = group.label;

                html.push(`
                    <button class="js-tpl-cat tpl-cat" data-tag="${tag}" >
                        ${helpers.getTagIcon(label)}

                        <span>${label}</span>

                        <span class="tpl-cat__count"> ${list.length} </span>
                    </button>
                `);

            });

            holder.innerHTML = html.join('');

            const currentTag = filter.getValue('tag')?.toLowerCase();

            holder
                .querySelectorAll('.js-tpl-cat')
                .forEach(btn => btn.classList.remove('is-active'));

            const activeBtn = holder.querySelector(`.js-tpl-cat[data-tag="${currentTag}"]`);

            if (activeBtn) {

                activeBtn.classList.add('is-active');

            } else {

                holder
                    .querySelector('.js-tpl-cat[data-tag=""]')
                    ?.classList.add('is-active');

            }
        },

        renderSections(products) {

            const container = document.querySelector('#js-product-categories');

            if (!container) return;

            if (!products.length) {
                container.innerHTML = `
                    <div class="tpl-empty">
                        <h3> No templates match that search. </h3>
                        <p> Try another keyword. </p>
                        <button id="js-clear-filters" > Clear filters </button>
                    </div>
                `;

                return;
            }
            const { filter, state, config } = TemplateFilter;

            const selectedTag = filter.getValue('tag');

            const tagGroups = this.buildTagMap(products);

            const groups = selectedTag
                ? (tagGroups[selectedTag]
                    ? { [selectedTag]: tagGroups[selectedTag] }
                    : {})
                : tagGroups;

            const perPage = selectedTag
                ? config.tagPerPage
                : config.defaultPerPage;


            const html = Object.entries(groups).map(([tag, group]) => {
                const list = group.products;
                const label = group.label;

                const visible = Math.min(
                    state.visibleItems[tag] ?? perPage,
                    list.length
                );

                state.visibleItems[tag] = visible;

                const hasMore = visible < list.length;

                return `
                    <section class="tpl-section js-tpl-container" data-tag="${tag.toLowerCase()}" >
                        <div class="tpl-section__head">
                            <h2 class="tpl-section__title"> ${label} </h2>

                            <span class="tpl-section__sub">
                                <strong> ${list.length} </strong>
                                templates
                            </span>
                        </div>

                        <div class="tpl-grid js-product-holder">
                            ${list
                        .slice(0, visible)
                        .map(item => this.renderProductItem(item))
                        .join('')
                    }
                        </div>

                        ${hasMore ? `
                            <div class="tpl-section__footer mt-7 text-center">
                                <button class="btn btn--primary js-show-more" data-tag="${tag}" >
                                    Show more
                                    ${TemplateIcon.arrowIcon}
                                </button>
                            </div>
                        ` : ''}
                    </section>
                `;

            }).join('');

            container.innerHTML = html
        },

        appendProducts(tag) {
            const { state, config } = TemplateFilter;

            const section = document.querySelector(`.js-tpl-container[data-tag="${tag}"]`);

            if (!section) return;

            const products = state.currentProducts;

            const perPage = state.isTagPage ? config.tagPerPage : config.defaultPerPage;

            const current = state.visibleItems[tag] ?? perPage;

            const next = products.slice(current, current + perPage);

            section
                .querySelector('.js-product-holder')
                .insertAdjacentHTML(
                    'beforeend',
                    next.map(item => this.renderProductItem(item)).join('')
                );

            state.visibleItems[tag] = current + next.length;

            if (state.visibleItems[tag] >= products.length) {
                section.querySelector('.tpl-section__footer')?.remove();
            }
        },

        updateActiveCategory(tag = '') {

            const holder = document.querySelector(
                TemplateFilter.config.categoriesTarget
            );

            if (!holder) return;

            holder
                .querySelectorAll('.js-tpl-cat')
                .forEach(btn => btn.classList.remove('is-active'));

            (
                holder.querySelector(`.js-tpl-cat[data-tag="${tag}"]`) ||
                holder.querySelector('.js-tpl-cat[data-tag=""]')
            )?.classList.add('is-active');

        },

        updateCategoryCounts() {
            const { config, state } = TemplateFilter;

            const holder = document.querySelector(config.categoriesTarget);

            if (!holder) return;

            holder.querySelectorAll('.js-tpl-cat').forEach(btn => {
                const tag = btn.dataset.tag.toLowerCase();

                const count = tag
                    ? (state.productsIgnoreTagMap[tag]?.products.length || 0)
                    : state.productsIgnoreTagCount;

                btn.querySelector('.tpl-cat__count').textContent = count;
            });
        },

        updateTotalTemplates() {
            document.querySelectorAll('.js-total-template').forEach(el => {
                el.textContent = TemplateFilter.state.productList.length;
            });
        }

    },

    events: {

        init() {
            this.searchChange();
            this.industryChange();
            this.categoryClick();
            this.sortChange();
            this.showMore();
        },

        searchChange() {
            const { config, state, filter } = TemplateFilter;
            const { debounce } = TemplateFilter.helpers;

            const input = document.querySelector(
                config.searchInputTarget
            );

            if (!input) return;

            input.addEventListener('input',
                debounce(e => {
                    state.searchKeyword = e.target.value.trim();

                    filter.apply();
                }, 200)
            );
        },

        industryChange() {

            const select = document.querySelector(
                TemplateFilter.config.industriesTarget
            );

            if (!select) return;

            select.addEventListener('change', () => {
                TemplateFilter.filter.setFilter(
                    'industry',
                    select.value
                );
            });
        },

        categoryClick() {
            const { helpers, config, ui, state, filter } = TemplateFilter;

            const holder = document.querySelector(
                config.categoriesTarget
            );

            if (!holder) return;

            holder.addEventListener('click', e => {

                const btn = e.target.closest('.js-tpl-cat');

                if (!btn) return;

                ui.updateActiveCategory(btn.dataset.tag);

                const url = new URL(window.location);

                if (btn.dataset.tag) {
                    url.searchParams.set(
                        'tag',
                        helpers.slugifyTag(btn.dataset.tag)
                    );
                } else {
                    url.searchParams.delete('tag');
                }

                history.replaceState({}, '', url);

                if (btn.dataset.tag) {
                    state.isTagPage = true;
                } else {
                    state.isTagPage = false;
                }

                filter.setFilter(
                    'tag',
                    btn.dataset.tag || ''
                );

            });
        },

        sortChange() {

            const select = document.querySelector('#js-tpl-sort');

            if (!select) return;

            select.addEventListener('change', e => {

                TemplateFilter.config.currentSort = e.target.value;

                const sortName = document.querySelector('#js-sort-name');
                if (sortName) {
                    const selectedText = e.target.selectedOptions[0].text.trim();
                    sortName.innerHTML = `Sorted by <span class="text-white">${selectedText}</span>`;
                }

                TemplateFilter.filter.apply();

            });
        },

        showMore() {
            const { state, filter, ui, helpers } = TemplateFilter;

            document.addEventListener('click', e => {

                const btn = e.target.closest('.js-show-more');
                if (!btn) return;

                e.preventDefault();

                if (!state.isTagPage) {

                    const url = new URL(window.location.href);

                    url.searchParams.set(
                        'tag',
                        helpers.slugifyTag(btn.dataset.tag)
                    );

                    // đổi URL nhưng KHÔNG reload
                    history.replaceState({}, '', url);

                    state.isTagPage = true;

                    const tag = btn.dataset.tag.toLowerCase();

                    filter.setFilter('tag', tag);

                    ui.updateActiveCategory(tag);

                    // scroll sau khi render lại products
                    requestAnimationFrame(() => {

                        const target = document.querySelector('#js-product-categories');

                        if (!target) return;

                        const top = target.getBoundingClientRect().top + window.scrollY - 350;

                        window.scrollTo({
                            top,
                            behavior: 'smooth'
                        });

                    });

                    return;
                }

                ui.appendProducts(btn.dataset.tag.toLowerCase());
            });
        }

    },

    filter: {

        init() {
            this.filterRemove();
            this.clearFilters();
            this.apply();
        },

        getValue(key) {
            return TemplateFilter.state.filterSelected
                .find(item => item.key === key)
                ?.value || '';
        },

        getFilteredProducts(ignoreTag = false) {
            const { state, helpers } = TemplateFilter;

            const keyword = state.searchKeyword;
            const keywordWords = keyword
                ? helpers.normalizeText(keyword).split(' ').filter(Boolean)
                : [];

            const industry = this.getValue('industry');
            const tag = ignoreTag ? '' : this.getValue('tag');

            return state.productList.filter(product => {

                const searchMatch =
                    !keywordWords.length ||
                    helpers.matchesKeywordWords(product.__titleNormalize, keywordWords);

                const industryMatch =
                    !industry ||
                    product.product_type === industry;

                const tagMatch =
                    !tag ||
                    product.__tagsLower.includes(tag);

                return searchMatch && industryMatch && tagMatch;
            });
        },

        apply() {
            const { state, ui, config } = TemplateFilter;

            let products = this.getFilteredProducts();
            products = this.sortProducts(products, config.currentSort);

            state.currentProducts = products;

            const productsIgnoreTag = this.getFilteredProducts(true);

            state.productsIgnoreTagCount = productsIgnoreTag.length;
            state.productsIgnoreTagMap = ui.buildTagMap(productsIgnoreTag);

            state.visibleItems = {};

            ui.renderSections(products);
            ui.updateCategoryCounts();

            this.renderSelectedFilters();
            this.updateTemplateCount(products.length);
        },

        setFilter(key, value) {
            const { state } = TemplateFilter;

            let filters = state.filterSelected.filter(item => item.key !== key);

            if (value) {
                filters.push({
                    key,
                    value
                });
            }

            state.filterSelected = filters;

            this.apply();
        },

        removeFilter(key) {
            const { state, ui, config } = TemplateFilter;

            state.filterSelected = state.filterSelected.filter(item => item.key !== key);

            if (key === 'industry') {

                const select = document.querySelector(
                    config.industriesTarget
                );

                if (select) {
                    select.value = '';
                }
            }

            if (key === 'tag') {

                state.isTagPage = false;

                ui.updateActiveCategory();

                const url = new URL(window.location);

                url.searchParams.delete('tag');

                history.replaceState({}, '', url);
            }

            this.apply();

        },

        filterRemove() {
            const holder = document.querySelector(
                TemplateFilter.config.filterSelected
            );

            if (!holder) return;

            holder.addEventListener('click', e => {

                const chip = e.target.closest('[data-filter-key]');

                if (!chip) return;

                this.removeFilter(chip.dataset.filterKey);
            });
        },

        clearFilters() {
            const { state, config, ui } = TemplateFilter;

            document.addEventListener('click', e => {

                if (!e.target.closest('#js-clear-filters')) return;

                state.filterSelected = [];
                state.searchKeyword = '';
                state.visibleItems = {};
                state.isTagPage = false;

                const search = document.querySelector(
                    config.searchInputTarget
                );

                if (search) {
                    search.value = '';
                }

                const industry = document.querySelector(
                    config.industriesTarget
                );

                if (industry) {
                    industry.value = '';
                }

                ui.updateActiveCategory();


                // window.location.href = window.location.pathname;

                const url = new URL(window.location);
                url.searchParams.delete('tag');
                history.replaceState({}, '', url);

                // Render lại
                this.apply();
            });

        },

        renderSelectedFilters() {
            const { config, state, ui } = TemplateFilter;

            const holder = document.querySelector(config.filterSelected);
            if (!holder) return;

            const filters = state.filterSelected;
            if (!filters.length) {
                holder.innerHTML = '';
                return;
            }

            const groups = state.fullTagMap;

            holder.innerHTML = filters.map(item => {
                const label = item.key === 'tag'
                    ? groups[item.value]?.label || item.value
                    : item.value;
                return `
                    <span
                        class="tpl-results__active-tag"
                        data-filter-key="${item.key}"
                    >
                        <span>${label}</span>

                        <button aria-label="Remove filter">
                            <svg width="9" height="9" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
                                <path d="M4 4l8 8"></path>
                                <path d="M12 4l-8 8"></path>
                            </svg>
                        </button>
                    </span>
                `
            }).join('');

        },

        updateTemplateCount(count) {
            const el = document.querySelector('#js-template-count');

            if (el) {
                el.textContent = count;
            }
        },

        sortProducts(products, type) {
            return [...products].sort((a, b) => {
                switch (type) {
                    case 'created-descending':
                        return b.__date - a.__date;

                    case 'created-ascending':
                        return a.__date - b.__date;

                    case 'best-selling':
                        return (
                            b.__priority - a.__priority ||
                            b.__date - a.__date
                        );

                    case 'title-ascending':
                        return (a.title || '').localeCompare(b.title || '');

                    case 'title-descending':
                        return (b.title || '').localeCompare(a.title || '');

                    default:
                        return b.__date - a.__date;
                }
            });
        }
    },

    api: {
        async fetchAllProducts() {
            const CACHE_KEY = 'template_products_cache_v2';
            const CACHE_TTL = 30 * 60 * 1000; // cache data 30 mins

            try {
                const cached = sessionStorage.getItem(CACHE_KEY);
                if (cached) {
                    const { data, time } = JSON.parse(cached);

                    const isValid =
                        Date.now() - time < CACHE_TTL &&
                        Array.isArray(data) &&
                        data.every(p => typeof p.__titleNormalize === 'string');

                    if (isValid) return data;
                }

                const products = await this.fetchProducts();
                const filtered = this.filterProducts(products);

                sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: filtered, time: Date.now() }));

                return filtered;
            } catch (error) {
                console.error('Error loading products:', error);
                return [];
            }
        },

        async fetchProducts() {
            const { apiLimit } = TemplateFilter.config;

            const totalProducts = window.__productsCount || 1;
            const totalPages = Math.ceil(totalProducts / apiLimit);

            const requests = [];

            for (let page = 1; page <= totalPages; page++) {
                requests.push(
                    fetch(`${window.location.origin}/products.json?limit=${apiLimit}&page=${page}`)
                        .then(res => {
                            if (!res.ok) {
                                throw new Error(`Failed to load page ${page}`);
                            }

                            return res.json();
                        })
                );
            }

            const results = await Promise.all(requests);

            const ids = new Set();
            const products = [];

            results.forEach(({ products: items }) => {
                items.forEach(product => {
                    if (!ids.has(product.id)) {
                        ids.add(product.id);
                        products.push(product);
                    }
                });
            });

            return products;
        },

        filterProducts(products) {
            const { ignoreProducts } = TemplateFilter.config;
            const { normalizeText, getPriority } = TemplateFilter.helpers;

            return products
                .filter(product =>
                    !(product.tags || []).some(tag =>
                        ignoreProducts.includes(tag.toLowerCase())
                    )
                )
                .map(product => {

                    const tagsLower = (product.tags || []).map(t => t.toLowerCase());

                    product.__date = Date.parse(product.created_at) || 0;
                    product.__priority = getPriority(tagsLower);
                    product.__tagsLower = tagsLower;
                    product.__titleNormalize = normalizeText(product.title);

                    return product;
                });
        }

    },

    helpers: {
        slugifyTag(tag) {
            return tag.toLowerCase().trim().replace(/\s+/g, '-');
        },

        unslugifyTag(tag) {
            return tag.toLowerCase().replace(/-/g, ' ');
        },

        getTagIcon(tag) {
            const key = tag.toLowerCase().replace(/\s+/g, '_');

            return industriesIcon[key] || industriesIcon.default_icon;
        },

        debounce(fn, delay = 200) {
            let timer;

            return (...args) => {
                clearTimeout(timer);
                timer = setTimeout(() => { fn(...args); }, delay);
            };
        },

        getResizeImage(url, width = 640) {
            if (!url) return '';

            const separator = url.includes('?') ? '&' : '?';
            return `${url}${separator}width=${width}`;
        },


        normalizeText(str) {
            return (str || '')
                .toLowerCase()
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // bỏ dấu tiếng Việt nếu có
                .replace(/[-_|,.:;!?()[\]{}'"]/g, ' ') // thay ký tự đặc biệt bằng khoảng trắng
                .replace(/\s+/g, ' ')
                .trim();
        },

        getPriority(tagsLower) {
            const PRIORITY = {
                recommend: 3,
                trending: 2,
                featured: 1
            };

            let score = 0;

            for (const tag of tagsLower) {
                score = Math.max(score, PRIORITY[tag] || 0);
            }

            return score;
        },

        matchesKeywordWords(titleNormalize, words) {
            return words.every(word => titleNormalize.includes(word));
        },

    }
};


TemplateFilter.init();